import React, { useContext, useEffect, useState, useRef } from 'react';
import { Dropdown, Icon, Input, DatePicker, Checkbox, Avatar } from '../../common/UIComponents';
import { convertToArray, checkServiceExistance } from '../../helpers/common';
import { alertMessage } from '../../common/Common';
import I18n from '../../common/I18n';
import { Button, Spin } from 'antd';
import { OrderConfigContext } from '../../context/OrderConfigContext';
import { OrgContext } from '../../context/OrgContext';
import MoveTypeBadge from "../common/MoveTypeBadge";
import AppConfig from "../../config/AppConfig";
import { BusinessIcon, InvoiceIcon, QuoteIcon, TruckIcon, } from '../../common/CustomIcon';
import { RecoveryAndTransferIcon } from '../orders/OrderTypeIcons';
import { fetchGlobalSearch, fetchRecentSearches, saveSearchHistory } from '../../api/OrdersApi';
import { getOrderTypeIcon } from '../orders/OrderTypeIcons';
import { fetchOrgAccounts } from '../../api/Account';
import moment from 'moment';
import manlogo from '../../../assets/images/driver.png';
import { withRouter } from 'react-router-dom';
import Button_v2 from '../../stories/components/Button_v2';
import Copyable from '../common/Copyable';
import GlobalSearchHint from './GlobalSearchHint';


const ORDER_TYPE_ICON_DIMENSIONS = { width: 24, height: 24 };

const getDocumentIcon = (result, enhancedOrderTypes, omtByOrderLabel = {}) => {
    if (!result) return null;

    const { document_type, projectable_fields } = result;

    // For customer orders, use the order_label to get the specific icon.
    // Move-type-driven orgs (catalog index populated): the org-configured
    // MoveTypeBadge, matching the Type filter and the order grids; legacy
    // orgs keep the hardcoded SVG icon.
    if (document_type === 'customer_orders' && projectable_fields['order_label']) {
        const orderLabelKey = projectable_fields['order_label_key'];
        const configuredOmt = omtByOrderLabel[orderLabelKey];
        if (configuredOmt) {
            return (
                <MoveTypeBadge
                    shortCode={configuredOmt.short_code}
                    color={configuredOmt.display_color}
                    title={configuredOmt.alias_name}
                />
            );
        }
        // Move-order legs (SDR/SDD/MDR/MDD) have no catalog entry — the org's
        // MOVE type is keyed "M". Mirror TypeOfOrder.jsx: on a DB-badges org
        // render the leg short-form pill (SD/MP/MD) so the modal matches the
        // order grids; legacy orgs still fall through to the SVG icon.
        const MOVE_LABEL_TO_LEG = { SDR: "TR", SDD: "TD", MDR: "MR", MDD: "MD" };
        const legType = MOVE_LABEL_TO_LEG[orderLabelKey];
        if (legType && Object.keys(omtByOrderLabel).length > 0) {
            // Color/tooltip come from the org's MOVE catalog entry so the pill
            // matches the order grids (TypeOfOrder renders move rows the same
            // way: leg short form over the MOVE badge styling), not the legacy
            // bright-blue orderTypeColorMapping.
            const moveOmt = omtByOrderLabel["M"];
            return (
                <MoveTypeBadge
                    shortCode={AppConfig.orderTypeShortForms[legType]}
                    color={moveOmt?.display_color}
                    title={moveOmt?.alias_name || AppConfig.orderTypeLabels?.[legType]}
                />
            );
        }
        const resolvedCode = enhancedOrderTypes?.find(ot => ot.code === orderLabelKey)?.extraCode || orderLabelKey;
        return getOrderTypeIcon(resolvedCode, ORDER_TYPE_ICON_DIMENSIONS);
    }

    // For billings,
    if (document_type === 'billings') {
        return <Icon type="dollar" style={{ fontSize: '24px' }} />;
    }

    // For invoices, 
    if (document_type === 'account_invoices') {
        return <Icon component={() => <InvoiceIcon />} />;
    }

    // For drivers
    if (document_type === 'users') {
        return projectable_fields['image'] ? (
            <img
                src={projectable_fields['image']}
                alt="logo"
                style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    objectFit: "cover"
                }}
            />
        ) : (
            <Avatar src={manlogo} alt="DriverProfile" size={25} />
        );
    }

    // For accounts
    if (document_type === 'accounts') {
        return projectable_fields?.logo ? (
            <img
                src={projectable_fields.logo}
                alt="logo"
                style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    objectFit: "cover"
                }}
            />
        ) : (
            <Icon component={() => <BusinessIcon />} />
        );
    }

    // For vehicles/trucks
    if (document_type === 'fleets' ) {
        return <Icon component={() => <TruckIcon />} />

    }

    // For Quotes
    if (document_type === 'quote_orders') {
        return <Icon component={() => <QuoteIcon />} />
    }
};


// TypeFilterDropdown (Orders as parent checkbox with nested options) - Antd v3
const TypeFilterDropdown = ({ visible, onVisibleChange, filters = {}, onFilterChange, onBatchFilterChange, filterRef, onFocus, isFocused, isInDropdownNavigation, dropdownInternalIndex, onDropdownNavigationChange }) => {
    const [expanded, setExpanded] = useState(true);
    const [filterText, setFilterText] = useState('');
    const { orderConfig, omtByOrderLabel = {} } = useContext(OrderConfigContext);
    const { orgSettings } = useContext(OrgContext);
    
    const labelsToIgnore = ["SDR", "SDD", "MDR", "MDD"];
    const excludedOrderTypes = convertToArray(orgSettings?.order_types) || [];
    
    const availableOrderTypes = excludedOrderTypes.length
        ? orderConfig?.enhancedOrderTypes?.filter(
            (order) => excludedOrderTypes.includes(order.extraCode) || excludedOrderTypes.includes(order.code)
        ) || []
        : orderConfig?.enhancedOrderTypes || [];
    
    // Move-type-driven orgs (service_level_flow_enabled -> omtByOrderLabel
    // populated): the Orders list comes from the org's ACTIVE catalog (badge +
    // alias) and the filter VALUE is the order_label CODE — the backend accepts
    // codes as-is and keeps the legacy name->code reverse map for old clients /
    // saved searches. Legacy orgs keep the hardcoded names (value = label).
    const dbOrderTypes = Object.keys(omtByOrderLabel);
    const allOrderTypes = dbOrderTypes.length
        ? dbOrderTypes
            .filter((label) => !labelsToIgnore.includes(label))
            .map((label) => ({
                label: omtByOrderLabel[label].alias_name || label,
                value: label,
                code: label,
                badge: omtByOrderLabel[label]
            }))
        : availableOrderTypes
            .filter(order => !labelsToIgnore.includes(order.code))
            .map(order => ({
                label: order.label,
                value: order.label, 
                code: order.code
            }));
    
    const filteredOrderTypes = allOrderTypes.filter(orderType => {
        if (!filterText.trim()) return true;
        const searchLower = filterText.toLowerCase();
        return orderType.label?.toLowerCase().includes(searchLower);
    });

    const orderValues = allOrderTypes.map(t => t.value);
    const selectedOrders = filters.order_labels || [];

    const allChecked = selectedOrders.length === orderValues.length && orderValues.length > 0;
    const indeterminate = selectedOrders.length > 0 && selectedOrders.length < orderValues.length;

    // toggle all orders
    const handleOrdersParentChange = (e) => {
        const checked = e.target.checked;
        onFilterChange('order_labels', checked ? [...orderValues] : []);
    };

    // toggle a single order type
    const handleOrderTypeChange = (value, checked) => {
        const current = filters.order_labels || [];
        const updated = checked ? [...current, value] : current.filter(v => v !== value);
        onFilterChange('order_labels', updated);
    };

    const handleCategoryChange = (category, checked) => {
        onFilterChange(category, checked);
    };

    const toggleExpand = (e) => {
        e.stopPropagation();
        setExpanded(!expanded);
    };

    useEffect(() => {
        if (!visible) {
            setFilterText('');
        }
    }, [visible]);

    const menu = (
        <div 
            style={{ 
                padding: '12px', 
                minWidth: '300px', 
                background: '#fff', 
                borderRadius: '4px', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)' ,
                minWidth: '280px', 
                maxHeight: '380px', 
                overflowY: 'auto',  
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Orders section */}
                <div>
                    {(
                        <div style={{ padding: '8px 0', marginBottom: '4px' }}>
                            <Input
                                placeholder="Search order types..."
                                value={filterText}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    setFilterText(e.target.value);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                prefix={<Icon type="search" />}
                                size='small'
                                style={{ marginBottom: '4px' }}
                            />
                        </div>
                    )}
                    {/* Parent checkbox */}
                    <div 
                        style={{ 
                            padding: '4px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            transition: 'background-color 0.3s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f7ff'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                        <Icon 
                            type="caret-right" 
                            onClick={toggleExpand}
                            style={{ 
                                fontSize: '12px', 
                                marginRight: '4px',
                                transition: 'transform 0.2s',
                                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                cursor: 'pointer'
                            }}
                        />
                        <Checkbox
                            indeterminate={indeterminate}
                            checked={allChecked}
                            onChange={handleOrdersParentChange}
                            onClick={(e) => e.stopPropagation()}
                            style={{ fontWeight: 700 }}
                        >
                            Orders
                        </Checkbox>
                    </div>

                    {/* Nested options */}
                    {expanded && (
                        <div style={{ paddingLeft: 24 }}>
                            {filteredOrderTypes.length === 0 ? (
                                <div style={{ padding: '8px', color: '#999' }}>No order types match your search</div>
                            ) : (
                                filteredOrderTypes.map(type => (
                                    <div 
                                        key={type.value} 
                                        style={{ 
                                            padding: '4px 8px',
                                            cursor: 'pointer',
                                            transition: 'background-color 0.3s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f7ff'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <Checkbox
                                            checked={selectedOrders.includes(type.value)}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                handleOrderTypeChange(type.value, e.target.checked);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {type.badge ? (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                    <MoveTypeBadge shortCode={type.badge.short_code} color={type.badge.display_color} title={type.label} />
                                                    {type.label}
                                                </span>
                                            ) : (
                                                type.label
                                            )}
                                        </Checkbox>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                {/* Other categories */}

								<div 
                    style={{ 
                        padding: '4px 8px',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f7ff'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                    <Checkbox
                        checked={filters.include_billings || false}
                        onChange={(e) => handleCategoryChange('include_billings', e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                    >
                        Billings
                    </Checkbox>
                </div>

                <div 
                    style={{ 
                        padding: '4px 8px',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f7ff'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                    <Checkbox
                        checked={filters.include_invoices || false}
                        onChange={(e) => handleCategoryChange('include_invoices', e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                    >
                        Invoices
                    </Checkbox>
                </div>

                <div 
                    style={{ 
                        padding: '4px 8px',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f7ff'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                    <Checkbox
                        checked={filters.include_accounts || false}
                        onChange={(e) => handleCategoryChange('include_accounts', e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                    >
                        Accounts
                    </Checkbox>
                </div>

                <div 
                    style={{ 
                        padding: '4px 8px',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f7ff'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                    <Checkbox
                        checked={filters.include_drivers || false}
                        onChange={(e) => handleCategoryChange('include_drivers', e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                    >
                        Drivers
                    </Checkbox>
                </div>

                <div 
                    style={{ 
                        padding: '4px 8px',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f7ff'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                    <Checkbox
                        checked={filters.include_equipment || false}
                        onChange={(e) => handleCategoryChange('include_equipment', e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                    >
                        Equipments
                    </Checkbox>
                </div>

								<div 
                    style={{ 
                        padding: '4px 8px',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f7ff'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                    <Checkbox
                        checked={filters.include_quotes || false}
                        onChange={(e) => handleCategoryChange('include_quotes', e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                    >
                        Quotes
                    </Checkbox>
                </div>
            </div>
        </div>
    );

    const getTypeCount = () => {
        let count = 0;
        if (selectedOrders.length > 0) count += selectedOrders.length;
        if (filters.include_invoices) count++;
        if (filters.include_accounts) count++;
        if (filters.include_billings) count++;
        if (filters.include_quotes) count++;
        if (filters.include_drivers) count++;
        if (filters.include_equipment) count++;
        return count;
    };

    const typeCount = getTypeCount();
    const isActive = typeCount > 0;

    const handleClear = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (onBatchFilterChange) {
            onBatchFilterChange({
                order_labels: [],
                include_invoices: false,
                include_accounts: false,
                include_billings: false,
                include_quotes: false,
                include_drivers: false,
                include_equipment: false
            });
        } else {
            onFilterChange('order_labels', []);
            onFilterChange('include_invoices', false);
            onFilterChange('include_accounts', false);
            onFilterChange('include_billings', false);
            onFilterChange('include_quotes', false);
            onFilterChange('include_drivers', false);
            onFilterChange('include_equipment', false);
        }
    };

    return (
        <Dropdown
            overlay={menu}
            trigger={['click']}
            visible={visible}
            onVisibleChange={onVisibleChange}
            overlayStyle={{ padding: 0 }}
        >
            <div
                ref={filterRef}
                tabIndex={3}
                onFocus={() => {
                    if (onFocus) onFocus();
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onVisibleChange(!visible);
                    }
                }}
                style={{
                    display: 'flex',
                    padding: '4px 12px',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '24px',
                    background: isActive ? '#586FCC' : '#DEE2F5',
                    cursor: 'pointer',
                    color: isActive ? '#FFF' : '#47599B',
                    fontFamily: 'Lato',
                    fontSize: '16px',
                    fontWeight: 700,
                    lineHeight: '24px',
                    outline: 'none',
                    boxShadow: isFocused ? '0 0 0 2px #586FCC' : 'none'
                }}
            >
                {isActive ? `${typeCount} Type${typeCount > 1 ? 's' : ''}` : 'Type'} 
                {isActive && (
                    <Icon
                        type="close"
                        onClick={handleClear}
                        style={{ 
                            width: 12, 
                            height: 12, 
                            fontSize: 12,
                            color: '#FFF',
                            cursor: 'pointer'
                        }}
                    />
                )}
                {!isActive && (
                    <Icon
                        type="caret-down"
                        style={{ width: 12, height: 12, fontSize: 12 }}
                    />
                )}
            </div>
        </Dropdown>
    );
};


const DateFilterDropdown = ({ visible, onVisibleChange, filters, onFilterChange, onBatchFilterChange, filterRef, onFocus, isFocused, isInDropdownNavigation, dropdownInternalIndex, onDropdownNavigationChange }) => {
    const dateTypes = [
        { key: 'appointment_date', label: I18n.t("general.appointment") },
        { key: 'created_at', label: 'Creation Date' },
        { key: 'approval_date', label: 'Approved Date' },
        { key: 'completion_date', label: 'Completed Date' },
    ];

    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [selectedDateTypes, setSelectedDateTypes] = useState([]);

    useEffect(() => {
        if (visible) {
            const activeDateTypes = dateTypes.filter(dt => filters[dt.key]).map(dt => dt.key);
            setSelectedDateTypes(activeDateTypes);

            if (activeDateTypes.length > 0) {
                const firstDateType = filters[activeDateTypes[0]];
                setFromDate(firstDateType?.from ? moment(firstDateType.from) : null);
                setToDate(firstDateType?.to ? moment(firstDateType.to) : null);
            } else {
                setFromDate(null);
                setToDate(null);
            }
        }
    }, [visible]);

    const handleDateTypeToggle = (dateType, checked) => {
        if (checked) {
            setSelectedDateTypes([...selectedDateTypes, dateType]);
        } else {
            setSelectedDateTypes(selectedDateTypes.filter(dt => dt !== dateType));
        }
    };

    const handleApply = () => {
        const filterUpdates = {};
        
        selectedDateTypes.forEach(dateType => {
            if (fromDate && toDate) {
                filterUpdates[dateType] = {
                    from: fromDate.format('YYYY-MM-DD'),
                    to: toDate.format('YYYY-MM-DD')
                };
            } else {
                filterUpdates[dateType] = null;
            }
        });

        dateTypes.forEach(dt => {
            if (!selectedDateTypes.includes(dt.key)) {
                filterUpdates[dt.key] = null;
            }
        });

        if (onBatchFilterChange && Object.keys(filterUpdates).length > 0) {
            onBatchFilterChange(filterUpdates);
        } else {
            Object.keys(filterUpdates).forEach(key => {
                onFilterChange(key, filterUpdates[key]);
            });
        }

        onVisibleChange(false);
    };

    const handleCancel = () => {
        onVisibleChange(false);
    };

    const handleFromChange = (date) => {
        setFromDate(date);

        if (date && toDate) {
            if (toDate.isBefore(date, 'day')) {
                setToDate(date);
            }
        }
    };


    const getDateCount = () => {
        return dateTypes.filter(dt => {
            const dateFilter = filters[dt.key];
            return dateFilter && dateFilter.from && dateFilter.to;
        }).length;
    };

    const dateCount = getDateCount();
    const isActive = dateCount > 0;

    const handleClear = (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (onBatchFilterChange) {
            const clearedFilters = {};
            dateTypes.forEach(dt => {
                clearedFilters[dt.key] = null;
            });
            onBatchFilterChange(clearedFilters);
        } else {
            dateTypes.forEach(dt => {
                onFilterChange(dt.key, null);
            });
        }
    };

    const menu = (
        <div 
            style={{ 
                padding: '12px',
                width: '300px',
                background: '#fff', 
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* From Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '2px' }}>
                <label style={{
                    fontFamily: 'Lato',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#000'
                }}>
                    From
                </label>
                <DatePicker
                    size="large"
                    value={fromDate}
                    onChange={handleFromChange} 
                    format="MM/DD/YYYY"
                    className="custom-datepicker"
                    placeholder="Select date"
                    suffixIcon={<Icon type="calendar" style={{ color: '#5B6FCC' }} />}
                />
            </div>

            {/* To Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{
                    fontFamily: 'Lato',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#000'
                }}>
                    To
                </label>
                <DatePicker
                    size="large"
                    value={toDate}
                    onChange={(date) => setToDate(date)}
                    format="MM/DD/YYYY"
                    className="custom-datepicker"
                    placeholder="Select date"
                    suffixIcon={<Icon type="calendar" style={{ color: '#5B6FCC' }} />}
                    disabledDate={(current) => {
                        if (!fromDate) return false;
                        return current && current.isBefore(fromDate, 'day');
                    }}
                />
            </div>


            {/* Date Type Checkboxes */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
            {dateTypes.map((dateType) => {
                const isChecked = selectedDateTypes.includes(dateType.key);
                return (
                    <div
                        key={dateType.key}
                        style={{ 
                            padding: '2px 0',
                            cursor: 'pointer'
                        }}
                    >
                        <Checkbox
                            checked={isChecked}
                            onChange={(e) => handleDateTypeToggle(dateType.key, e.target.checked)}
                            style={{
                                fontFamily: 'Lato',
                                fontSize: '14px',
                                fontWeight: isChecked ? 600 : 400,
                                color: '#000'
                            }}
                        >
                            <span style={{ fontWeight: isChecked ? 600 : 400 }}>
                                {dateType.label}
                            </span>
                        </Checkbox>
                    </div>
                );
            })}
        </div>
            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                <Button_v2
                    className="medium-btn outline"
                    onClick={handleCancel}
                    text="Cancel"
                    style={{
                        flex: 1,
                        height: '40px',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontWeight: 500
                    }}
                />
                <Button_v2
                    className="medium-btn raised"
                    onClick={handleApply}
                    text="Apply"
                    style={{
                        flex: 1,
                        height: '40px',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontWeight: 500
                    }}
                />
            </div>

        </div>
    );

    return (
        <Dropdown
            overlay={menu}
            trigger={['click']}
            visible={visible}
            onVisibleChange={onVisibleChange}
        >
            <div
                ref={filterRef}
                tabIndex={4}
                onFocus={() => {
                    if (onFocus) onFocus();
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onVisibleChange(!visible);
                    }
                }}
                style={{
                    display: 'flex',
                    padding: '4px 12px',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '24px',
                    background: isActive ? '#586FCC' : '#DEE2F5',
                    cursor: 'pointer',
                    color: isActive ? '#FFF' : '#47599B',
                    fontFamily: 'Lato',
                    fontSize: '16px',
                    fontWeight: 700,
                    lineHeight: '24px',
                    outline: 'none',
                    boxShadow: isFocused ? '0 0 0 2px #586FCC' : 'none'
                }}
            >
                {isActive ? `${dateCount} Date${dateCount > 1 ? 's' : ''}` : 'Date'}
                {isActive && (
                    <Icon
                        type="close"
                        onClick={handleClear}
                        style={{ 
                            width: 12, 
                            height: 12, 
                            fontSize: 12,
                            color: '#FFF',
                            cursor: 'pointer'
                        }}
                    />
                )}
                {!isActive && (
                    <Icon
                        type="caret-down"
                        style={{ width: 12, height: 12, fontSize: 12 }}
                    />
                )}
            </div>
        </Dropdown>
    );
};

const AccountFilterDropdown = ({ visible, onVisibleChange, filters, onFilterChange, accounts, loading, filterRef, onFocus, isFocused, isInDropdownNavigation, dropdownInternalIndex, onDropdownNavigationChange }) => {
    const [expanded, setExpanded] = useState(true);
    const [filterText, setFilterText] = useState('');
    const accountIds = filters.account_ids || [];
    const allAccountIds = accounts.map(acc => acc.id);
    
    const filteredAccounts = accounts.filter(account => {
        if (!filterText.trim()) return true;
        const searchLower = filterText.toLowerCase();
        return account.name?.toLowerCase().includes(searchLower) || 
               account.code?.toLowerCase().includes(searchLower);
    });

    const handleAccountChange = (accountId, checked) => {
        const current = filters.account_ids || [];
        const updated = checked ? [...current, accountId] : current.filter(id => id !== accountId);
        onFilterChange('account_ids', updated);
    };

    const toggleExpand = (e) => {
        e.stopPropagation();
        setExpanded(!expanded);
    };

    const getAccountCount = () => {
        const count = accountIds.length;
        if (count === 0) return 0;
        if (count === allAccountIds.length && allAccountIds.length > 0) return 0; // "All" means no filter
        return count;
    };

    const accountCount = getAccountCount();
    const isActive = accountCount > 0;

    const handleClear = (e) => {
        e.stopPropagation();
        onFilterChange('account_ids', []);
        // Note: handleFilterChange will trigger search if searchText exists
    };

    useEffect(() => {
        if (!visible) {
            setFilterText('');
        }
    }, [visible]);

    const menu = (
        <div 
            style={{ 
                padding: '12px', 
                minWidth: '280px', 
                maxHeight: '380px', 
                overflowY: 'auto', 
                background: '#fff', 
                borderRadius: '4px', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)' 
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {loading ? (
                <div style={{ padding: '8px', textAlign: 'center' }}>
                    <Spin size="small" /> Loading accounts...
                </div>
            ) : accounts.length === 0 ? (
                <div style={{ padding: '8px' }}>No accounts available</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* Filter Input */}
                    <Input
                        placeholder="Search accounts..."
                        value={filterText}
                        onChange={(e) => {
                            e.stopPropagation();
                            setFilterText(e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        prefix={<Icon type="search" />}
                        style={{ marginBottom: '4px' }}
                        size='small'
                    />
                    

                    {expanded && (
                        <div style={{ paddingLeft: 24 }}>
                            {filteredAccounts.length === 0 ? (
                                <div style={{ padding: '8px', color: '#999' }}>No accounts match your search</div>
                            ) : (
                                filteredAccounts.map((account) => (
                                    <div 
                                        key={account.id} 
                                        style={{ 
                                            padding: '4px 8px',
                                            cursor: 'pointer',
                                            transition: 'background-color 0.3s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6f7ff'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <Checkbox
                                            checked={accountIds.includes(account.id)}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                handleAccountChange(account.id, e.target.checked);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {account.name} ({account.code})
                                        </Checkbox>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <Dropdown
            overlay={menu}
            trigger={['click']}
            visible={visible}
            onVisibleChange={onVisibleChange}
            getPopupContainer={(trigger) => trigger.parentElement}
        >
            <div
                ref={filterRef}
                tabIndex={5}
                onFocus={() => {
                    if (onFocus) onFocus();
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onVisibleChange(!visible);
                    }
                }}
                style={{
                    display: 'flex',
                    padding: '4px 12px',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '24px',
                    background: isActive ? '#586FCC' : '#DEE2F5',
                    cursor: 'pointer',
                    color: isActive ? '#FFF' : '#47599B',
                    fontFamily: 'Lato',
                    fontSize: '16px',
                    fontWeight: 700,
                    lineHeight: '24px',
                    outline: 'none',
                    boxShadow: isFocused ? '0 0 0 2px #586FCC' : 'none'
                }}
            >
                {isActive ? `${accountCount} Account${accountCount > 1 ? 's' : ''}` : 'Account'}
                {isActive && (
                    <Icon
                        type="close"
                        onClick={handleClear}
                        style={{ 
                            width: 12, 
                            height: 12, 
                            fontSize: 12,
                            color: '#FFF',
                            cursor: 'pointer'
                        }}
                    />
                )}
                {!isActive && (
                    <Icon
                        type="caret-down"
                        style={{ width: 12, height: 12, fontSize: 12 }}
                    />
                )}
            </div>
        </Dropdown>
    );
};

const EXCLUDED_FIELD_KEYS = ['logo', 'profile_image', 'image', 'customer_order_id', 'account_id',
                             'order_label_key', 'pickup_nav_route_id', 'delivery_nav_route_id', 
                             'delivery_driver_id', 'pickup_driver_id', 'wh_location_id', 'type_of_order', 'is_file_generated', 'is_locked','customer_order_ids',
                             'is_deleted_order', 'is_deleted_billing'
                             ];
const FIELDS_WITHOUT_COPY_ICON = ['order_label', 'status'];

const isDeletedFlag = (value) => value === true || value === 'true';

const isDeletedOrderResult = (result) => {
    if (!result) return false;
    const fields = result.projectable_fields || {};
    return isDeletedFlag(fields.is_deleted_order) || isDeletedFlag(fields.is_deleted_billing);
};

const getIdentityValue = (result) => {
    const docType = result.document_type || '';
    const fields = result.projectable_fields || {};
    
    switch (docType) {
        case 'customer_orders':
            return fields['customer_order_number'] || result.value;
        case 'billings':
            return fields['order_number'] || result.value;
        case 'account_invoices':
            return fields['frequency_invoice_number'] || result.value;
        case 'accounts':
            return fields['name'] || result.value;
        case 'fleets':
            return fields['name'] || fields['Fleet Number'] || result.value;
        case 'quote_orders':
            return fields['quote_order_number'] || result.value;
        case 'users':
            const firstName = fields['first_name'] || '';
            const lastName = fields['last_name'] || '';
            return `${firstName} ${lastName}`.trim() || result.value;
        default:
            return result.value || '';
    }
};

    const RecentSearchItem = ({ result, onClick, onHover, onLeave, isSelected, tabIndex, onKeyDown, searchTerm,isPreviewFocused, enhancedOrderTypes, omtByOrderLabel = {} }) => {
        const suggestionText = result.suggestion_text || '';
        const displayType = result.document_type_display || result.document_type || '';
        const highlightedText = result.highlighted_text || result.value || '';
        const identityValue = getIdentityValue(result);
        
        const displayIdentity = `# ${identityValue}`;
        
        const markMatch = highlightedText.match(/<mark>(.*?)<\/mark>/i);
        const matchedText = markMatch ? markMatch[1] : null;
    
        let highlightedSuggestion = suggestionText;
        
        if (matchedText) {
            const escapedMatch = matchedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            
            // Check if the original search has wildcards
            const hasWildcard = searchTerm && (searchTerm.includes('*'));
            
            if (hasWildcard) {
                // Wildcard search - use backend highlighting pattern
                const startsWithMark = highlightedText.trim().startsWith('<mark>');
                const endsWithMark = highlightedText.trim().endsWith('</mark>');
                
                highlightedSuggestion = suggestionText.replace(/([^:,]+):\s*([^,]+)/g, (fullMatch, fieldName, fieldValue) => {
                    const trimmedValue = fieldValue.trim();
                    let highlightedValue = trimmedValue;
                    
                    if (startsWithMark && !endsWithMark) {
                        // 121* pattern
                        const regex = new RegExp(`^(${escapedMatch})`, 'i');
                        highlightedValue = trimmedValue.replace(regex, '<mark>$1</mark>');
                    } else if (!startsWithMark && endsWithMark) {
                        // *121 pattern
                        const regex = new RegExp(`(${escapedMatch})$`, 'i');
                        highlightedValue = trimmedValue.replace(regex, '<mark>$1</mark>');
                    } else {
                        // *121* pattern
                        const regex = new RegExp(`(${escapedMatch})`, 'gi');
                        highlightedValue = trimmedValue.replace(regex, '<mark>$1</mark>');
                    }
                    
                    return `${fieldName}: ${highlightedValue}`;
                });
            } else {
                // Normal search - highlight ALL occurrences globally
                const regex = new RegExp(`(${escapedMatch})`, 'gi');
                highlightedSuggestion = suggestionText.replace(regex, '<mark>$1</mark>');
            }
        }

    return (
        <div
            tabIndex={tabIndex || -1}
            style={{
                display: 'flex',
                padding: '6px 12px',
                alignItems: 'center',
                gap: '12px',
                alignSelf: 'stretch',
                borderRadius: '8px',
                border: '1px solid #DDE5EB',
                background: isSelected ? '#DEE2F5' : '#FFF',
                cursor: 'pointer',
                transition: 'all 0.3s',
                outline: 'none'
            }}
            onFocus={() => {
                if (onHover) onHover();
            }}
            onBlur={() => {
                if (onLeave) onLeave();
            }}
            onMouseEnter={(e) => {
                if (!isSelected) {
                    e.currentTarget.style.backgroundColor = '#DEE2F5';
                }
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                if (onHover) onHover();
            }}
            onMouseLeave={(e) => {
                if (isSelected) {
                    e.currentTarget.style.backgroundColor = '#DEE2F5';
                } else {
                    e.currentTarget.style.backgroundColor = '#fff';
                }
                e.currentTarget.style.boxShadow = 'none';
                if (onLeave) onLeave();
            }}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {

                    if (!isPreviewFocused) {
                        e.preventDefault();
                        e.stopPropagation();
                        onClick();
                    } else {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                } else if (onKeyDown) {
                    onKeyDown(e);
                }
            }}
        >
            {getDocumentIcon(result, enhancedOrderTypes, omtByOrderLabel)}

            <div style={{
                flex: '1 1 0',     
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                overflow: 'hidden',
                minWidth: 0         
            }}>
                {/* Identity Value - Top (NO HIGHLIGHTING) */}
                <div style={{
                    alignSelf: 'stretch',
                    color: '#111314',
                    fontFamily: 'Lato',
                    fontSize: '15px',
                    fontWeight: 700,
                    lineHeight: '18px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                    {displayIdentity}
                    {isDeletedOrderResult(result) && (
                        <span style={{ color: '#C24141', marginLeft: 8, fontWeight: 700 }}>Deleted</span>
                    )}
                </div>
                
                {/* Suggestion Text with Wildcard-Aware Highlight - Bottom */}
                <div style={{
                    alignSelf: 'stretch',
                    color: '#66717B',
                    fontFamily: 'Lato',
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '16px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}
                    dangerouslySetInnerHTML={{ __html: highlightedSuggestion }}
                />
            </div>

            <div style={{
                display: 'flex',
                padding: '4px 8px',
                alignItems: 'center',
                gap: '8px',
                borderRadius: '24px',
                border: '1px solid #DEE2F5',
                color: '#586FCC',
                fontFamily: 'Lato',
                fontSize: '12px',
                fontWeight: 700,
                lineHeight: '16px'
            }}>
                {displayType}
            </div>
        </div>
    );
};

const isEmail = (text) => {
    if (!text || typeof text !== 'string') return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(text.trim());
};

const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        
        // Format: Nov 26, 2025 12:00 AM +05:30
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short'
        });
    } catch (e) {
        return dateString;
    }
};

const shouldExcludeField = (key, documentType) => {
    const lowerKey = key.toLowerCase();
    
    if (key === 'profile_image' || key === 'image') return true;
    if (key === 'logo') return true;
    
    if (key === 'wh_location_id') return true;
    
    if (key === 'account_id') return true;
    
    if (lowerKey.includes('driver') && lowerKey.includes('id')) return false;
    if (lowerKey.includes('route') && lowerKey.includes('id')) return false;
    
    if (lowerKey.endsWith('_id') || lowerKey.endsWith('_ids') || lowerKey === 'id') return true;
    
    return false;
};

const DetailItem = ({ label, value, tabIndex, isDriverField, onDriverClick, fieldKey, isFocused, onFocus, onKeyDown, copyableRef }) => {
    if (!value && value !== 0) return null;
    
    const [isHovered, setIsHovered] = useState(false);
    const copyableComponentRef = useRef(null);
    const displayValue = Array.isArray(value) ? value.join(', ') : String(value);
    const isEmailValue = isEmail(displayValue);
    
    const cleanLabel = String(label).replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
    
    const lowerLabel = cleanLabel.toLowerCase();
    const isDateField = lowerLabel.includes('date') || lowerLabel.includes('time');
    const formattedValue = isDateField ? formatDateTime(displayValue) : displayValue;
    
    const isStatusField = lowerLabel === 'status';
    
    const shouldShowCopyIcon = !isStatusField && 
        (!fieldKey || !FIELDS_WITHOUT_COPY_ICON.includes(fieldKey.toLowerCase()));
    
    const isFocusedOrHovered = isFocused || isHovered;
    
    return (
        <div
            ref={copyableRef}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'stretch',
                padding: '10px 0',
                tabIndex: tabIndex || -1,
                background: isFocusedOrHovered ? '#DEE2F5' : 'transparent',
                borderRadius: '4px',
                outline: isFocused ? '2px solid #586FCC' : 'none',
                outlineOffset: '2px'
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocus={(e) => {
                if (onFocus) onFocus();
            }}
            onKeyDown={(e) => {
                if (onKeyDown) {
                    onKeyDown(e);
                }
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center' }}>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                color: '#66717B',
                fontFamily: 'Lato',
                fontSize: '11px',
                fontWeight: 600,
                lineHeight: '16px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '180px'
            }}>
                {cleanLabel}
            </div>
            <div
                       style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                color: '#111314',
                fontFamily: 'Lato',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
                    flex: 1,
                    minWidth: 0
                }}>
                    {isStatusField ? (
                        <div
                            style={{
                                display: 'inline-flex',
                                padding: '1px 8px',
                                alignItems: 'center',
                                borderRadius: '24px',
                                background: '#E8E9EB',
                                fontFamily: 'Lato',
                                fontSize: '10px',
                                fontWeight: 700,
                                lineHeight: '24px'
                            }}
                        >
                            {formattedValue}
                        </div>
                    ) : isDriverField ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <a
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDriverClick?.();
                                }}
                                style={{
                                    color: '#586FCC',
                                    cursor: 'pointer',
                                    fontWeight: 600
                                }}
                            >
                                {formattedValue}
                            </a>
                            <span style={{ fontSize: '16px' }} title="View in Routes"><DispatchIcon/></span>
                        </div>
                    ) : isEmailValue ? (
                        <a
                            href={`mailto:${displayValue}`}
                            style={{
                                color: '#586FCC',
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '180px'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {formattedValue}
                        </a>
                    ) : (
                        <span>{formattedValue}</span>
                    )}
                </div>
            </div>

                {isFocusedOrHovered && shouldShowCopyIcon && (
                    <div 
                        style={{ marginLeft: '8px', alignSelf: 'center', fontSize: "20px", display: 'flex', alignItems: 'center' }}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        <Copyable ref={copyableComponentRef} text={displayValue} color="#586FCC" />
                    </div>
                )}
            </div>
        </div>
    );
};

const formatFieldName = (fieldName) => {
    if (!fieldName) return '';
    return fieldName
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const SearchOptions = (props) => {
 const { filter, handleFilterPlaceHolderChange, elementSize, onSearch, handleFilterValueChange, orderNumberSearch, searchType, searchText, setSearchText, closeFilterWindow, WarehouseContext: WarehouseContextProp, onClose, history } = props;
    const { endUserName, currentOrg, currentUser } = useContext(OrgContext);
    const { filterPlaceHolder: savedFilter, setFilterPlaceHolder, orderConfig, omtByOrderLabel = {} } = useContext(OrderConfigContext);
    const [hoveredItem, setHoveredItem] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);

    // Filter states
    const [filters, setFilters] = useState({
        order_labels: [],
        include_invoices: false,
				include_billings: false,
				include_quotes: false,
        include_accounts: false,
        include_drivers: false,
        include_equipment: false,
        appointment_date: null,
        created_at: null,
        approval_date: null,
        completion_date: null,
        account_ids: [],
  });

    // Accounts state
    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);

    const [typeDropdownVisible, setTypeDropdownVisible] = useState(false);
    const [dateDropdownVisible, setDateDropdownVisible] = useState(false);
    const [accountDropdownVisible, setAccountDropdownVisible] = useState(false);

    const [showViewAllButton, setShowViewAllButton] = useState(false);
    const [totalSuggestions, setTotalSuggestions] = useState(null);
    const [recentSearches, setRecentSearches] = useState([]);
    const [recentSearchesLoading, setRecentSearchesLoading] = useState(false);
    const [viewingAllResults, setViewingAllResults] = useState(false);
    const searchInputRef = useRef(null);
    const isInitialMount = useRef(true);
    const lastSearchParamsRef = useRef(null);
    const requestIdRef = useRef(0);
    const filterDebounceTimeoutRef = useRef(null);
    const [focusedElement, setFocusedElement] = useState('search'); 
    const [focusedPreviewIndex, setFocusedPreviewIndex] = useState(-1); 
    const [dropdownInternalIndex, setDropdownInternalIndex] = useState(-1); 
    const [isInDropdownNavigation, setIsInDropdownNavigation] = useState(false); 
    const clearButtonRef = useRef(null);
    const typeFilterRef = useRef(null);
    const dateFilterRef = useRef(null);
    const accountFilterRef = useRef(null);
    const clearAllRef = useRef(null);
    const viewAllRef = useRef(null);
    const previewFieldsRefs = useRef([]);

    useEffect(() => {
        if (currentOrg?.id) {
            getAccounts();
            loadRecentSearches();
        }
    }, [currentOrg?.id]);

    const loadRecentSearches = async () => {
        if (!currentOrg?.id) {
            return;
        }
        
        setRecentSearchesLoading(true);
        try {
            const response = await fetchRecentSearches(currentOrg.id);
            
            if (response?.recent_searches && Array.isArray(response.recent_searches)) {
                const mappedHistory = response.recent_searches
                    .map(h => h?.search_result)
                    .filter(Boolean);
                    
                setRecentSearches(mappedHistory);
                
                if (mappedHistory.length > 0 && (!searchText || searchText.trim().length === 0)) {
                    setSelectedIndex(0);
                    setHasUserInteracted(false);
                    const historyItem = mappedHistory[0];
                    const actualResult = historyItem?.search_result || historyItem;
                    setSelectedItem(actualResult);
                } else if (mappedHistory.length === 0) {
                    setSelectedItem(null);
                    setHoveredItem(null);
                }
            } else {
                setRecentSearches([]);
                setSelectedItem(null);
                setHoveredItem(null);
            }
        } catch (error) {
            console.error('Error details:', {
                message: error?.message,
                status: error?.response?.status
            });
            setRecentSearches([]);
            setSelectedItem(null);
            setHoveredItem(null);
        } finally {
            setRecentSearchesLoading(false);
        }
    };

    useEffect(() => {
        if (currentOrg?.id && (!searchText || searchText.trim().length === 0)) {
            setSearchResults([]);
            isInitialMount.current = false;
        }
    }, [currentOrg?.id]);

    useEffect(() => {
        previewFieldsRefs.current = [];
    }, [selectedItem, hoveredItem]);

    useEffect(() => {
        if (searchInputRef.current) {
            setTimeout(() => {
                const inputElement = searchInputRef.current?.input || searchInputRef.current;
                if (inputElement) {
                    inputElement.focus();
                    setFocusedElement('search');
                }
            }, 100);
        }
    }, []);

    const getAccounts = () => {
        if (!currentOrg?.id) {
            return;
        }
        
        setAccountsLoading(true);
        fetchOrgAccounts(currentOrg.id)
            .then((result) => {
                if (result?.success) {
                    const accounts = result.accounts || [];
                    setAccounts(accounts);
                } else {
                    setAccounts([]);
                }
            })
            .catch((error) => {
                setAccounts([]);
            })
            .finally(() => {
                setAccountsLoading(false);
            });
    };

    const handleFilterChange = (key, value) => {
        const newFilters = {
            ...filters,
            [key]: value
        };
        setFilters(newFilters);
    };

    const handleBatchFilterChange = (updates) => {
        const newFilters = {
            ...filters,
            ...updates
        };
        setFilters(newFilters);
    };

    const hasActiveFilters = () => {
        return (
            (filters.order_labels?.length > 0) ||
            filters.include_invoices ||
            filters.include_accounts ||
            filters.include_billings ||
            filters.include_quotes ||
            filters.include_drivers ||
            filters.include_equipment ||
            filters.appointment_date ||
            filters.created_at ||
            filters.approval_date ||
            filters.completion_date ||
            (filters.account_ids?.length > 0)
        );
    };

    const handleClearAll = () => {
        const clearedFilters = {
            order_labels: [],
            include_invoices: false,
            include_billings: false,
            include_quotes: false,
            include_accounts: false,
            include_drivers: false,
            include_equipment: false,
            appointment_date: null,
            created_at: null,
            approval_date: null,
            completion_date: null,
            account_ids: [],
        };
        setFilters(clearedFilters);
    };

    useEffect(() => {
        if (!searchText || searchText.trim().length === 0) {
            setSearchResults([]);
            setSelectedItem(null);
            setHoveredItem(null);
            setSelectedIndex(0);
            setHasUserInteracted(false);
            setShowViewAllButton(false);
            setTotalSuggestions(null);
            setViewingAllResults(false);
            lastSearchParamsRef.current = null;
            if (filterDebounceTimeoutRef.current) {
                clearTimeout(filterDebounceTimeoutRef.current);
                filterDebounceTimeoutRef.current = null;
            }
            return;
        }

        if (!isInitialMount.current && searchText.trim().length < 3) {
            setSearchResults([]);
            setSelectedItem(null);
            setHoveredItem(null);
            setSelectedIndex(0);
            setHasUserInteracted(false);
            setShowViewAllButton(false);
            setTotalSuggestions(null);
            setViewingAllResults(false);
            if (filterDebounceTimeoutRef.current) {
                clearTimeout(filterDebounceTimeoutRef.current);
                filterDebounceTimeoutRef.current = null;
            }
            return;
        }

        
        if (searchText.trim().length >= 3) {
            setSelectedItem(null);
            setHoveredItem(null);
        }

        if (filterDebounceTimeoutRef.current) {
            clearTimeout(filterDebounceTimeoutRef.current);
            filterDebounceTimeoutRef.current = null;
        }

        const delay = searchText.trim().length >= 3 ? 700 : 300;

        filterDebounceTimeoutRef.current = setTimeout(() => {
                performSearch(searchText, false);
                setViewingAllResults(false);

            if (isInitialMount.current) {
                isInitialMount.current = false;
            }
        }, delay);

        return () => {
            if (filterDebounceTimeoutRef.current) {
                clearTimeout(filterDebounceTimeoutRef.current);
                filterDebounceTimeoutRef.current = null;
            }
        };
    }, [searchText, currentOrg?.id, filters]);

    useEffect(() => {
        const showRecentHistory = !searchText || searchText.trim().length === 0;
        const currentResults = showRecentHistory ? recentSearches : searchResults;
        
        if (loading) {
            setSelectedItem(null);
            setHoveredItem(null);
            return;
        }
        
        if (currentResults.length > 0) {
            if (!hasUserInteracted && selectedIndex === 0) {
                if (showRecentHistory) {
                    const historyItem = recentSearches[0];
                    const actualResult = historyItem?.search_result || historyItem;
                    setSelectedItem(actualResult);
                } else {
                    setSelectedItem(searchResults[0]);
                }
            } else if (hasUserInteracted) {
                if (showRecentHistory && selectedIndex >= 0 && selectedIndex < recentSearches.length) {
                    const historyItem = recentSearches[selectedIndex];
                    const actualResult = historyItem?.search_result || historyItem;
                    setSelectedItem(actualResult);
                } else if (!showRecentHistory && selectedIndex >= 0 && selectedIndex < searchResults.length) {
                    setSelectedItem(searchResults[selectedIndex]);
                } else {
                    setSelectedItem(null);
                }
            }
        } else {
            setSelectedItem(null);
            setHoveredItem(null);
        }
    }, [selectedIndex, searchResults, recentSearches, hasUserInteracted, searchText, loading]);

      const performSearch = async (term, viewAllParam = false) => {
        if (!currentOrg?.id) {
            return;
        }
        
        if (!term || term.trim().length === 0) {
            return;
        }

        requestIdRef.current += 1;
        const currentRequestId = requestIdRef.current;

        setSelectedItem(null);
        setHoveredItem(null);
        setLoading(true);
        try {
          const payload = {
            search_term: term,
            filters: {
              order_labels: filters.order_labels?.length > 0 ? filters.order_labels : undefined,
              include_invoices: filters.include_invoices || undefined,
							include_billings: filters.include_billings || undefined,
							include_quotes: filters.include_quotes || undefined,
              include_accounts: filters.include_accounts || undefined,
              include_drivers: filters.include_drivers || undefined,
              include_equipment: filters.include_equipment || undefined,
              appointment_date: filters.appointment_date || undefined,
              created_at: filters.created_at || undefined,
              approval_date: filters.approval_date || undefined,
              completion_date: filters.completion_date || undefined,
              account_ids: filters.account_ids?.length > 0 ? filters.account_ids : undefined,
              view_all: viewAllParam
            }
          };

          Object.keys(payload.filters).forEach(key => {
            if (payload.filters[key] === undefined) {
              delete payload.filters[key];
            }
          });

          
          const response = await fetchGlobalSearch(currentOrg.id, term, viewAllParam, payload);
          


          if (currentRequestId !== requestIdRef.current) {
            return; 
          }

          if (response.results) {
            setSearchResults(response.results);
            setSelectedIndex(0);
            setHasUserInteracted(false);
            if (response.results.length > 0) {
                setSelectedItem(response.results[0]);
            } else {
                setSelectedItem(null);
                setHoveredItem(null);
            }
            
            const totalCount = response.total_suggestions || response.total_count || response.results.length;
            setTotalSuggestions(totalCount);


            if (!viewAllParam && totalCount > 5 && response.results.length < totalCount) {
              setShowViewAllButton(true);
            } else {
              setShowViewAllButton(false);
            }
            
            setViewingAllResults(viewAllParam);
          }
        } catch (error) {
          if (currentRequestId === requestIdRef.current) {
            console.error('Error details:', { 
                message: error.message, 
                stack: error.stack,
                response: error.response 
            });
            setSearchResults([]);
            setSelectedItem(null);
            setHoveredItem(null);
            setShowViewAllButton(false);
            setTotalSuggestions(null);
          }
        } finally {
          if (currentRequestId === requestIdRef.current) {
            setLoading(false);
          }
        }
      };



    const navigateToDispatch = (item, legType) => {
        const fields = item?.projectable_fields || {};
        
        
        let status, completionDate, whLocationId, routeId;
        if (legType === 'pickup') {
            status = 'ALL';
            completionDate = fields['pickup_completion_date'];
            whLocationId = fields['wh_location_id'];
            routeId = fields['pickup_nav_route_id'];
            

        } else {
            status = 'ALL';
            completionDate = fields['delivery_completion_date'];
            whLocationId = fields['wh_location_id'];
            routeId = fields['delivery_nav_route_id'];
            

        }
        
        if (!routeId) {
            return;
        }
        
        let filterDate;
        
        
        if (completionDate) {
            filterDate = moment(completionDate);
        } else {
            filterDate = moment();
        }
        
        const navigationState = {
            pathname: "/routes",
            state: {
                filter: status,
                routeId: routeId,
                dateFilter: {
                    fromDate: filterDate.format('YYYY-MM-DD'),
                    toDate: filterDate.format('YYYY-MM-DD'),
                }
            }
        };
        
        
        if (onClose) onClose();
        
        history.push(navigationState);
    };

    const handleKeyDown = (e) => {
        const currentResults = showRecentHistory ? recentSearches : searchResults;
        
        if (isInDropdownNavigation && (focusedElement === 'type' || focusedElement === 'date' || focusedElement === 'account')) {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
                setDropdownInternalIndex(prev => prev + 1);
                return;
        } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                if (dropdownInternalIndex > 0) {
                    setDropdownInternalIndex(prev => prev - 1);
                }
                return;
            } else if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
              
                return;
            } else if (e.key === 'Tab') {
                e.preventDefault();
                setIsInDropdownNavigation(false);
                setDropdownInternalIndex(-1);
                
                if (focusedElement === 'type') {
                    setTypeDropdownVisible(false);
                } else if (focusedElement === 'date') {
                    setDateDropdownVisible(false);
                } else if (focusedElement === 'account') {
                    setAccountDropdownVisible(false);
                }
                
                handleTabNavigation(e.shiftKey);
                return;
            }
        }
        
        if ((e.key === 'Enter' || e.key === ' ') && (focusedElement === 'type' || focusedElement === 'date' || focusedElement === 'account')) {
            e.preventDefault();
            if (focusedElement === 'type' && !typeDropdownVisible) {
                setTypeDropdownVisible(true);
                setIsInDropdownNavigation(true);
                setDropdownInternalIndex(0);
            } else if (focusedElement === 'date' && !dateDropdownVisible) {
                setDateDropdownVisible(true);
                setIsInDropdownNavigation(true);
                setDropdownInternalIndex(0);
            } else if (focusedElement === 'account' && !accountDropdownVisible) {
                setAccountDropdownVisible(true);
                setIsInDropdownNavigation(true);
                setDropdownInternalIndex(0);
            }
            return;
        }
        
        if (e.key === 'Enter') {
            if ((focusedElement.startsWith('result-') || focusedElement === 'results') && focusedElement !== 'preview') {
                const resultIndex = focusedElement.startsWith('result-') 
                    ? parseInt(focusedElement.split('-')[1]) 
                    : selectedIndex;
                
                if (resultIndex >= 0 && resultIndex < currentResults.length) {
                    const itemToSelect = currentResults[resultIndex];
                    const actualResult = showRecentHistory && itemToSelect.search_result 
                        ? itemToSelect.search_result 
                        : itemToSelect;
                    
                    handleResultClick(actualResult, resultIndex);
                    
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }
            }
        }
        
        if (focusedElement === 'preview' && e.key === 'ArrowDown') {
            e.preventDefault();
            const currentItem = hoveredItem || selectedItem;
            if (currentItem) {
                const projectableFields = Object.entries(currentItem.projectable_fields || {})
                    .filter(([key, value]) => {
                        if (!value) return false;
                        const docType = currentItem?.document_type || '';
                        return !shouldExcludeField(key, docType) && !EXCLUDED_FIELD_KEYS.includes(key.toLowerCase());
                    });
                
                const detailsFields = Object.entries(currentItem.details || {})
                    .filter(([key, value]) => {
                        if (!value && value !== 0) return false;
                        const docType = currentItem?.document_type || '';
                        return !shouldExcludeField(key, docType);
                    });
                
                const totalFields = projectableFields.length + detailsFields.length;
                
                if (focusedPreviewIndex < totalFields - 1) {
                    const newIndex = focusedPreviewIndex + 1;
                    setFocusedPreviewIndex(newIndex);
                    setTimeout(() => {
                        if (previewFieldsRefs.current[newIndex]) {
                            previewFieldsRefs.current[newIndex].focus();
                            // Scroll into view
                            previewFieldsRefs.current[newIndex].scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest',
                                inline: 'nearest'
                            });
                        }
                    }, 0);
                }
            }
        } else if (focusedElement === 'preview' && e.key === 'ArrowUp') {
            e.preventDefault();
            if (focusedPreviewIndex > 0) {
                const newIndex = focusedPreviewIndex - 1;
                setFocusedPreviewIndex(newIndex);
                setTimeout(() => {
                    if (previewFieldsRefs.current[newIndex]) {
                        previewFieldsRefs.current[newIndex].focus();
                        // Scroll into view
                        previewFieldsRefs.current[newIndex].scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                            inline: 'nearest'
                        });
                    }
                }, 0);
            } else {
                setFocusedElement(`result-${selectedIndex}`);
                setTimeout(() => {
                    const resultElement = document.querySelector(`[tabindex="${8 + selectedIndex}"]`);
                    if (resultElement) resultElement.focus();
                }, 0);
            }
        }
        
        if (e.key === 'ArrowRight' && !isInDropdownNavigation) {
            if (focusedElement.startsWith('result-') || focusedElement === 'results') {
                const resultIndex = focusedElement.startsWith('result-') 
                    ? parseInt(focusedElement.split('-')[1]) 
                    : selectedIndex;
                
                if (resultIndex >= 0 && resultIndex < currentResults.length) {
                    const itemToSelect = currentResults[resultIndex];
                    const actualResult = showRecentHistory && itemToSelect.search_result 
                        ? itemToSelect.search_result 
                        : itemToSelect;
                    
                    if (!selectedItem && !hoveredItem) {
                        setSelectedItem(actualResult);
                        setHoveredItem(actualResult);
                    }
                    
                    e.preventDefault();
                    setFocusedElement('preview');
                    setFocusedPreviewIndex(0);
                    setTimeout(() => {
                        if (previewFieldsRefs.current[0]) {
                            previewFieldsRefs.current[0].focus();
                            previewFieldsRefs.current[0].scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest',
                                inline: 'nearest'
                            });
                        }
                    }, 0);
                    return;
                }
            }
        } else if (e.key === 'ArrowLeft' && !isInDropdownNavigation) {
            if (focusedElement === 'preview' && currentResults.length > 0) {
                e.preventDefault();
                setFocusedElement(`result-${selectedIndex}`);
                setTimeout(() => {
                    const resultElement = document.querySelector(`[tabindex="${8 + selectedIndex}"]`);
                    if (resultElement) resultElement.focus();
                }, 0);
                return;
            }
        }
        
        if (e.key === 'ArrowDown' && !isInDropdownNavigation) {
            if (focusedElement === 'results' || focusedElement.startsWith('result-') || (currentResults.length > 0 && focusedElement !== 'preview')) {
            e.preventDefault();
            setHasUserInteracted(true);
                const newIndex = (selectedIndex + 1) % currentResults.length;
                setSelectedIndex(newIndex);
                setFocusedElement(`result-${newIndex}`);
                setTimeout(() => {
                    const resultElement = document.querySelector(`[tabindex="${8 + newIndex}"]`);
                    if (resultElement) resultElement.focus();
                }, 0);
            }
        } else if (e.key === 'ArrowUp' && !isInDropdownNavigation) {
            if (focusedElement === 'results' || focusedElement.startsWith('result-') || (currentResults.length > 0 && focusedElement !== 'preview')) {
                e.preventDefault();
                setHasUserInteracted(true);
                const newIndex = (selectedIndex - 1 + currentResults.length) % currentResults.length;
                setSelectedIndex(newIndex);
                setFocusedElement(`result-${newIndex}`);
                setTimeout(() => {
                    const resultElement = document.querySelector(`[tabindex="${8 + newIndex}"]`);
                    if (resultElement) resultElement.focus();
                }, 0);
            }
        } else if (e.key === 'Tab') {
            e.preventDefault();
            setHasUserInteracted(true);
            
            if (typeDropdownVisible) setTypeDropdownVisible(false);
            if (dateDropdownVisible) setDateDropdownVisible(false);
            if (accountDropdownVisible) setAccountDropdownVisible(false);
            setIsInDropdownNavigation(false);
            setDropdownInternalIndex(-1);
            
            handleTabNavigation(e.shiftKey);
        }
    };

    const handleTabNavigation = (shiftKey) => {
        const currentResults = showRecentHistory ? recentSearches : searchResults;
        
        const filterElements = [];
        if (searchText) {
            filterElements.push('search');
            filterElements.push('clear');
        } else {
            filterElements.push('search');
        }
        filterElements.push('type', 'date', 'account');
        if (hasActiveFilters()) {
            filterElements.push('clearAll');
        }
        if (showViewAllButton && !viewingAllResults) {
            filterElements.push('viewAll');
        }
        if (currentResults.length > 0) {
            filterElements.push('results');
        }
        if (selectedItem || hoveredItem) {
            filterElements.push('preview');
        }
        
        if (focusedElement.startsWith('result-')) {
            const resultIndex = parseInt(focusedElement.split('-')[1]);
            if (shiftKey) {
                if (resultIndex === 0) {
                    const lastFilterIndex = filterElements.indexOf('results') - 1;
                    if (lastFilterIndex >= 0) {
                        const lastFilter = filterElements[lastFilterIndex];
                        setFocusedElement(lastFilter);
                        focusElement(lastFilter);
                    }
                } else {
                    const prevIndex = resultIndex - 1;
                    setSelectedIndex(prevIndex);
                    setFocusedElement(`result-${prevIndex}`);
                    setTimeout(() => {
                        const resultElement = document.querySelector(`[tabindex="${8 + prevIndex}"]`);
                        if (resultElement) resultElement.focus();
                    }, 0);
                }
            } else {
                if (selectedItem || hoveredItem) {
                    setFocusedElement('preview');
                    setFocusedPreviewIndex(0);
                    setTimeout(() => {
                        if (previewFieldsRefs.current[0]) {
                            previewFieldsRefs.current[0].focus();
                            previewFieldsRefs.current[0].scrollIntoView({
                                behavior: 'smooth',
                                block: 'nearest',
                                inline: 'nearest'
                            });
                        }
                    }, 0);
            } else {
                    setFocusedElement('search');
                    focusElement('search');
                }
            }
        } else if (focusedElement === 'preview') {
            if (shiftKey) {
                if (currentResults.length > 0) {
                    setFocusedElement(`result-${selectedIndex}`);
                    setTimeout(() => {
                        const resultElement = document.querySelector(`[tabindex="${8 + selectedIndex}"]`);
                        if (resultElement) resultElement.focus();
                    }, 0);
                }
            } else {
                setFocusedElement('search');
                focusElement('search');
            }
        } else {
            const currentIndex = filterElements.indexOf(focusedElement);
            if (shiftKey) {
                if (currentIndex > 0) {
                    const prevElement = filterElements[currentIndex - 1];
                    setFocusedElement(prevElement);
                    focusElement(prevElement);
                } else {
                    if (focusedElement === 'preview' && currentResults.length > 0) {
                        const lastIndex = currentResults.length - 1;
                        setSelectedIndex(lastIndex);
                        setFocusedElement(`result-${lastIndex}`);
                        setTimeout(() => {
                            const resultElement = document.querySelector(`[tabindex="${8 + lastIndex}"]`);
                            if (resultElement) resultElement.focus();
                        }, 0);
                    } else {
                        const lastElement = filterElements[filterElements.length - 1];
                        setFocusedElement(lastElement);
                        focusElement(lastElement);
                    }
                }
            } else {
                if (currentIndex < filterElements.length - 1) {
                    const nextElement = filterElements[currentIndex + 1];
                    if (nextElement === 'results' && currentResults.length > 0) {
                        setSelectedIndex(0);
                        setFocusedElement('result-0');
                        setTimeout(() => {
                            const resultElement = document.querySelector(`[tabindex="8"]`);
                            if (resultElement) resultElement.focus();
                        }, 0);
                    } else if (nextElement === 'preview' && (selectedItem || hoveredItem)) {
                        setFocusedElement('preview');
                        setFocusedPreviewIndex(0);
                        setTimeout(() => {
                            if (previewFieldsRefs.current[0]) {
                                previewFieldsRefs.current[0].focus();
                            }
                        }, 0);
                    } else {
                        setFocusedElement(nextElement);
                        focusElement(nextElement);
                    }
                } else {
                    setFocusedElement('search');
                    focusElement('search');
                }
            }
        }
    };

    const focusElement = (elementName) => {
        switch (elementName) {
            case 'search':
                setTimeout(() => {
                    const inputElement = searchInputRef.current?.input || searchInputRef.current;
                    if (inputElement) inputElement.focus();
                }, 0);
                break;
            case 'clear':
                setTimeout(() => {
                    if (clearButtonRef.current) {
                        const clearElement = clearButtonRef.current;
                        if (clearElement.focus) {
                            clearElement.focus();
                        } else if (clearElement.parentElement) {
                            clearElement.parentElement.focus();
                        }
                    }
                }, 0);
                break;
            case 'type':
                setTimeout(() => {
                    if (typeFilterRef.current) typeFilterRef.current.focus();
                }, 0);
                break;
            case 'date':
                setTimeout(() => {
                    if (dateFilterRef.current) dateFilterRef.current.focus();
                }, 0);
                break;
            case 'account':
                setTimeout(() => {
                    if (accountFilterRef.current) accountFilterRef.current.focus();
                }, 0);
                break;
            case 'clearAll':
                setTimeout(() => {
                    if (clearAllRef.current) clearAllRef.current.focus();
                }, 0);
                break;
            case 'viewAll':
                setTimeout(() => {
                    if (viewAllRef.current) {
                        const viewAllElement = viewAllRef.current;
                        if (viewAllElement.focus) {
                            viewAllElement.focus();
                        } else if (viewAllElement.button) {
                            viewAllElement.button.focus();
                        }
                    }
                }, 0);
                break;
            case 'results':
                break;
            case 'preview':
                setTimeout(() => {
                    if (previewFieldsRefs.current[0]) {
                        previewFieldsRefs.current[0].focus();
                        setFocusedPreviewIndex(0);
                        previewFieldsRefs.current[0].scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                            inline: 'nearest'
                        });
                    }
                }, 0);
                break;
        }
    };

    const handleResultClick = (result, index) => {
        if (!result) {
            return;
        }
        
        
        setSelectedItem(result);
        if (index !== undefined) {
            setSelectedIndex(index);
        }

            const historyData = {
                search_term: searchText.trim(),
                clicked_result: result,
                filters: filters,
                view_all: false
            };
            
            saveSearchHistory(historyData)
                .then(() => console.log('Search history saved'))
                .catch(err => {
                    console.error('Failed to save search history:', err);
                });

        // Navigate based on document_type
        const documentType = result.document_type;
        const documentId = result.document_id || result.id;


        // Perform navigation FIRST, before closing modal
        if (documentType === 'customer_orders' && documentId) {
            history.push(`/orders/${documentId}/details`);
        } else if (documentType === 'accounts' && documentId) {
            history.push('/accounts', { activeRecordId: documentId });
        } else if (documentType === 'billings') {
            if (!checkServiceExistance("OPSBLS")) {
                alertMessage("You don't have access to this page or the page doesn't exist", "error");
                return;
            }
            const orderId = result?.projectable_fields?.customer_order_id;
            history.push('/billing', { activeRecordId: orderId });
        } else if (documentType === 'account_invoices' && documentId) {
            const invoiceNumber = result?.projectable_fields?.['Invoice Number'] || result?.projectable_fields?.['frequency_invoice_number'] || '';
            const accountId = result?.projectable_fields?.account_id || '';
            const status = result?.projectable_fields?.status || '';
            const invoiceType = result?.projectable_fields?.account_frequency_type || '';
            const approvalDate = result?.projectable_fields?.approval_date || '';
            const invoiceApprovalDate = result?.projectable_fields?.invoice_approval_date || '';
            const isFileGenerated = result?.projectable_fields?.is_file_generated || 'false';
            const isLocked = result?.projectable_fields?.is_locked || 'false';
            const customerOrderIds = result?.projectable_fields?.customer_order_ids ?? [];

            
            history.push('/invoices', { 
              activeRecordId: documentId,
              invoiceNumber: invoiceNumber,
              accountId: accountId,
              status: status,
              invoiceType: invoiceType,
              approvalDate: approvalDate,
              invoiceApprovalDate: invoiceApprovalDate,
              isFileGenerated: isFileGenerated,
              isLocked: isLocked,
              customerOrderIds: customerOrderIds
            });
        } else if (documentType === 'fleets' && documentId) {
            history.push('/trucks', { activeRecordId: documentId });
        } else if (documentType === 'users' && documentId) {
            history.push('/drivers', { activeRecordId: documentId });
        } else if (documentType === 'quote_orders' && documentId) {
            history.push('/quotes', { activeRecordId: documentId });
        } else {
        }

        // Clear search and close modal AFTER navigation
        setSearchText('');
        handleFilterPlaceHolderChange({
            ...filter,
            search_order_token: '',
            search_key_value: '',
        });

        // Close modal with a small delay to ensure navigation happens first
        setTimeout(() => {
            if (onClose) {
                onClose();
            }
        }, 50);
    };

    const showRecentHistory = !searchText || searchText.trim().length === 0;

    return (
        <div style={{
            display: 'flex',
            width: '100%',
            height: '100%',
            alignItems: 'flex-start'
        }}>
            {/* Left Section */}
            <div style={{
                display: 'flex',
                padding: '16px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '16px',
                flex: '1 0 0',
                height: '100%',
                overflow: 'hidden'
            }}>
                <style>
                    {`
            .globalSearchInput::placeholder {
              color: #8897A4 !important;
              font-family: Lato;
              font-size: 16px;
              font-weight: 400;
              line-height: 24px;
            }
            .globalSearchInputGroup {
              display: flex;
              padding: 4px;
              align-items: flex-start;
              gap: 4px;
              align-self: stretch;
              border-radius: 8px;
              border: 2px solid #586FCC;
              background: #FFF;
            }
            .globalSearchInputGroup:focus-within {
              box-shadow: 0 0 0 2px rgba(88, 111, 204, 0.2);
            }
            .globalSearchInputGroup .ant-input {
              border: none !important;
              box-shadow: none !important;
              outline: none !important;
            }
            .globalSearchInputGroup .ant-input:focus,
            .globalSearchInputGroup .ant-input:hover {
              border: none !important;
              box-shadow: none !important;
              outline: none !important;
            }
          `}
              </style>

              {/* Part 1: Search title, input field, dropdowns */}
              <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '16px',
                  alignSelf: 'stretch'
              }}>
                  {/* Search Title with Close Button */}
                  <div style={{
                      display: 'flex',
                      height: '40px',
                      alignItems: 'center',
                      gap: '24px',
                      alignSelf: 'stretch'
                  }}>
                      <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          flex: '1 0 0',
                          alignSelf: 'stretch',
                          color: '#273156',
                          fontFamily: 'Poppins',
                          fontSize: '24px',
                          fontWeight: 600,
                          lineHeight: '32px'
                      }}>
                          Search
                      </div>
                  </div>

                  {/* Input Text Field */}
                  <div className="globalSearchInputGroup" style={{ position: 'relative' }}>
                      <Input
                          ref={searchInputRef}
                          autoFocus={true}
                          placeholder="Search for anything..."
                          value={searchText}
                          tabIndex={1}
                          style={{
                              fontSize: '14px',
                              width: '100%',
                              border: 'none',
                              padding: '1px 1px',
                              paddingRight: searchText ? '32px' : '1px'
                          }}
                          className='globalSearchInput'
                          onFocus={() => setFocusedElement('search')}
                          onChange={(e) => {
                              handleFilterPlaceHolderChange({
                                  ...filter,
                                  search_order_token: e.target.value,
                                  search_key_value: e.target.value,
                              });
                              setSearchText(e.target.value);
                          }}
                          onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const currentResults = showRecentHistory ? recentSearches : searchResults;
                                  
                                  // Get the item at the current selectedIndex
                                  if (selectedIndex >= 0 && selectedIndex < currentResults.length) {
                                      const itemToSelect = currentResults[selectedIndex];
                                      
                                      // If it's from recent history, extract the search_result
                                      const resultToHandle = showRecentHistory && itemToSelect.search_result 
                                          ? itemToSelect.search_result 
                                          : itemToSelect;
                                      
                                      handleResultClick(resultToHandle, selectedIndex);
                                  } else if (selectedItem) {
                                      // Fallback to selectedItem if available
                                      
                                      // Check if selectedItem is from history
                                      const resultToHandle = selectedItem.search_result || selectedItem;
                                      handleResultClick(resultToHandle);
                                  }
                              } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  if (onClose) {
                                      onClose();
                                  }
                              } else {
                                  handleKeyDown(e);
                              }
                          }}
                      />
                      {searchText && (
                          <button
                              ref={clearButtonRef}
                              type="button"
                              tabIndex={2}
                              onClick={(e) => {
                                  e.stopPropagation();
                                  setSearchText('');
                                  handleFilterPlaceHolderChange({
                                      ...filter,
                                      search_order_token: '',
                                      search_key_value: '',
                                  });
                                  // Focus search field after clearing
                                  setTimeout(() => {
                                  if (searchInputRef.current) {
                                      const inputElement = searchInputRef.current?.input || searchInputRef.current;
                                      if (inputElement) {
                                          inputElement.focus();
                                              setFocusedElement('search');
                                          }
                                      }
                                  }, 0);
                              }}
                              onFocus={() => setFocusedElement('clear')}
                              onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setSearchText('');
                                      handleFilterPlaceHolderChange({
                                          ...filter,
                                          search_order_token: '',
                                          search_key_value: '',
                                      });
                                      // Focus search field after clearing
                                      setTimeout(() => {
                                          if (searchInputRef.current) {
                                              const inputElement = searchInputRef.current?.input || searchInputRef.current;
                                              if (inputElement) {
                                                  inputElement.focus();
                                                  setFocusedElement('search');
                                              }
                                          }
                                      }, 0);
                                  } else if (e.key === 'Tab') {
                                      handleKeyDown(e);
                                  }
                              }}
                              style={{
                                  position: 'absolute',
                                  right: '8px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  cursor: 'pointer',
                                  background: 'transparent',
                                  border: 'none',
                                  padding: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  outline: focusedElement === 'clear' ? '2px solid #586FCC' : 'none',
                                  borderRadius: '50%'
                              }}
                          >
                              <Icon
                                  type="close-circle"
                                  style={{
                                  color: '#586FCC',
                                  fontSize: '18px'
                              }}
                          />
                          </button>
                      )}
                  </div>

                  {/* Filter Dropdowns */}
                  <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      alignSelf: 'stretch',
                      flexWrap: 'wrap'
                  }}>
                      <TypeFilterDropdown
                          filterRef={typeFilterRef}
                          visible={typeDropdownVisible}
                          onVisibleChange={(newVisible) => {
                              setTypeDropdownVisible(newVisible);
                              if (!newVisible) {
                                  setIsInDropdownNavigation(false);
                                  setDropdownInternalIndex(-1);
                              }
                          }}
                          filters={filters}
                          onFilterChange={handleFilterChange}
                          onBatchFilterChange={handleBatchFilterChange}
                          onFocus={() => {
                              setFocusedElement('type');
                              // Close other dropdowns
                              if (dateDropdownVisible) setDateDropdownVisible(false);
                              if (accountDropdownVisible) setAccountDropdownVisible(false);
                          }}
                          isFocused={focusedElement === 'type'}
                          isInDropdownNavigation={isInDropdownNavigation && focusedElement === 'type'}
                          dropdownInternalIndex={focusedElement === 'type' ? dropdownInternalIndex : -1}
                          onDropdownNavigationChange={(index) => setDropdownInternalIndex(index)}
                      />
                      <DateFilterDropdown
                          filterRef={dateFilterRef}
                          visible={dateDropdownVisible}
                          onVisibleChange={(newVisible) => {
                              setDateDropdownVisible(newVisible);
                              if (!newVisible) {
                                  setIsInDropdownNavigation(false);
                                  setDropdownInternalIndex(-1);
                              }
                          }}
                          filters={filters}
                          onFilterChange={handleFilterChange}
                          onBatchFilterChange={handleBatchFilterChange}
                          onFocus={() => {
                              setFocusedElement('date');
                              // Close other dropdowns
                              if (typeDropdownVisible) setTypeDropdownVisible(false);
                              if (accountDropdownVisible) setAccountDropdownVisible(false);
                          }}
                          isFocused={focusedElement === 'date'}
                          isInDropdownNavigation={isInDropdownNavigation && focusedElement === 'date'}
                          dropdownInternalIndex={focusedElement === 'date' ? dropdownInternalIndex : -1}
                          onDropdownNavigationChange={(index) => setDropdownInternalIndex(index)}
                      />
                      <AccountFilterDropdown
                          filterRef={accountFilterRef}
                          visible={accountDropdownVisible}
                          onVisibleChange={(newVisible) => {
                              setAccountDropdownVisible(newVisible);
                              if (!newVisible) {
                                  setIsInDropdownNavigation(false);
                                  setDropdownInternalIndex(-1);
                              }
                          }}
                          filters={filters}
                          onFilterChange={handleFilterChange}
                          accounts={accounts}
                          loading={accountsLoading}
                          onFocus={() => {
                              setFocusedElement('account');
                              // Close other dropdowns
                              if (typeDropdownVisible) setTypeDropdownVisible(false);
                              if (dateDropdownVisible) setDateDropdownVisible(false);
                          }}
                          isFocused={focusedElement === 'account'}
                          isInDropdownNavigation={isInDropdownNavigation && focusedElement === 'account'}
                          dropdownInternalIndex={focusedElement === 'account' ? dropdownInternalIndex : -1}
                          onDropdownNavigationChange={(index) => setDropdownInternalIndex(index)}
                      />
                      {hasActiveFilters() && (
                          <div
                              ref={clearAllRef}
                              tabIndex={6}
                              onClick={handleClearAll}
                              onFocus={() => setFocusedElement('clearAll')}
                              onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleClearAll();
                                  } else if (e.key === 'Tab') {
                                      handleKeyDown(e);
                                  }
                              }}
                              style={{
                                  display: 'flex',
                                  padding: '4px 12px',
                                  alignItems: 'center',
                                  gap: '8px',
                                  cursor: 'pointer',
                                  color: '#586FCC',
                                  fontSize: '16px',
                                  fontWeight: 700,
                                  lineHeight: '24px',
                                  outline: focusedElement === 'clearAll' ? '2px solid #586FCC' : 'none',
                                  borderRadius: '4px',
                                  boxShadow: focusedElement === 'clearAll' ? '0 0 0 2px #586FCC' : 'none'
                              }}
                          >
                              Clear All
                          </div>
                      )}
                  </div>
              </div>

              {/* Part 2: Search Results Title */}
              {!showRecentHistory && searchResults.length > 0 && (
                  <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      alignSelf: 'stretch'
                  }}>
                      <div style={{
                          color: '#000',
                          fontFamily: 'Lato',
                          fontSize: '16px',
                          fontWeight: 700,
                          lineHeight: '24px'
                      }}>
                          {viewingAllResults ? `All Results` : 'Best Matches'}
                      </div>
                      {showViewAllButton && !viewingAllResults && totalSuggestions > 5 && (
                          <Button
                              ref={viewAllRef}
                              type="link"
                              size="small"
                              tabIndex={7}
                              onClick={() => {
                                  performSearch(searchText, true);
                              }}
                              onFocus={() => {
                                  setFocusedElement('viewAll');
                                  // Close all dropdowns when View All is focused
                                  if (typeDropdownVisible) setTypeDropdownVisible(false);
                                  if (dateDropdownVisible) setDateDropdownVisible(false);
                                  if (accountDropdownVisible) setAccountDropdownVisible(false);
                                  setIsInDropdownNavigation(false);
                                  setDropdownInternalIndex(-1);
                              }}
                              onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      performSearch(searchText, true);
                                  } else {
                                      handleKeyDown(e);
                                  }
                              }}
                              style={{
                                  padding: '0',
                                  height: 'auto',
                                  color: '#586FCC',
                                  fontFamily: 'Lato',
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  cursor: 'pointer',
                                  outline: focusedElement === 'viewAll' ? '2px solid #586FCC' : 'none',
                                  borderRadius: '4px',
                                  boxShadow: focusedElement === 'viewAll' ? '0 0 0 2px rgba(88, 111, 204, 0.2)' : 'none'
                              }}
                          >
                              View All
                          </Button>
                      )}
                  </div>
              )}
              {showRecentHistory &&  (
                  <div style={{
                      alignSelf: 'stretch',
                      color: '#000',
                      fontFamily: 'Lato',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: '24px'
                  }}>
                      Recent History
                  </div>
              )}

              {/* Part 3: Search Cards */}
              <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '6px',
                  alignSelf: 'stretch',
                  flex: '1 1 auto',
                  overflowY: (viewingAllResults && searchResults.length > 5) || (!viewingAllResults && (searchResults.length > 5 || recentSearches.length > 5)) ? 'auto' : 'visible',
                  minHeight: 0,
                  maxHeight: viewingAllResults ? '400px' : 'auto'
              }}>
                  {loading || recentSearchesLoading ? (
                      <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '100%',
                          padding: '20px'
                      }}>
                          <Spin size="large" />
                      </div>
                  ) : showRecentHistory && recentSearches.length > 0 ? (
                      recentSearches.map((result, index) => (
                          <RecentSearchItem
                              key={result.id || index}
                              result={result}
                              searchTerm=""
                              tabIndex={8 + index}
                              enhancedOrderTypes={orderConfig?.enhancedOrderTypes}
                              omtByOrderLabel={omtByOrderLabel}
                              onClick={() => {
                                  // Extract search_result from history object
                                  const actualResult = result.search_result || result;
                                  handleResultClick(actualResult, index);
                              }}
                              onHover={() => {
                                  setHasUserInteracted(true);
                                  setHoveredItem(result);
                                  setSelectedIndex(index);
                                  setFocusedElement(`result-${index}`);
                              }}
                              onLeave={() => setHoveredItem(null)}
                              isSelected={selectedIndex === index}
                              isPreviewFocused={focusedElement === 'preview'}
                              onKeyDown={(e) => {
                                  handleKeyDown(e);
                              }}
                          />
                      ))
                  ) : searchResults.length > 0 ? (
                      searchResults.map((result, index) => (
                          <RecentSearchItem
                              key={result.id}
                              result={result}
                              searchTerm={searchText}
                              tabIndex={8 + index}
                              enhancedOrderTypes={orderConfig?.enhancedOrderTypes}
                              omtByOrderLabel={omtByOrderLabel}
                              onClick={() => handleResultClick(result, index)}
                              onHover={() => {
                                  setHasUserInteracted(true);
                                  setHoveredItem(result);
                                  setSelectedIndex(index);
                                  setFocusedElement(`result-${index}`);
                              }}
                              onLeave={() => setHoveredItem(null)}
                              isSelected={selectedIndex === index}
                              isPreviewFocused={focusedElement === 'preview'}
                              onKeyDown={(e) => {
                                  handleKeyDown(e);
                              }}
                          />
                      ))
                  ) : searchText && searchText.trim().length > 0 ? (
                      <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '100%',
                          padding: '20px',
                          color: '#8897A4',
                          fontFamily: 'Lato',
                          fontSize: '14px'
                      }}>
                          No results found
                      </div>
                  ) : showRecentHistory && recentSearches.length === 0 && !loading && !recentSearchesLoading ? (
                      <GlobalSearchHint />
                  ) : null}
              </div>

              <div style={{
                  display: 'flex',
                  width: 'calc(100% + 32px)',
                  alignSelf: 'stretch',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  margin: '0 -16px 0',
                    padding: '2px 8px 2px 6px',   
                  borderTop: '1px solid #DEE2F5',
                    height: '22px',               
                    boxSizing: 'border-box',      
                    flexShrink: 0                 
              }}>
                  <div style={{
                      display: 'flex',
                      alignItems: 'center',
                        height: '100%',         
                      marginLeft: '10px',
                      marginBottom: '-16px',
                  }}>
                      <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '18px',
                            paddingRight: '8px',
                            height: '100%'       
                      }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>↑↓</span>
                          <span style={{
                              fontSize: '12px',
                              fontWeight: 400,
                                lineHeight: '16px'
                          }}>
                              Select
                          </span>
                      </div>

                      <div style={{
                          display: 'flex',
                          alignItems: 'center',
                            gap: '6px',
                            height: '100%'        
                      }}>
                          <span style={{
                              fontSize: '14px',
                              color: '#66717B',
                              fontWeight: 600,
                                display: 'inline-flex',    
                                alignItems: 'center',
                                paddingTop: 0
                          }}>
                          ↵  
                        </span>
                          <span style={{
                              fontSize: '12px',
                              fontWeight: 400,
                              lineHeight: '16px'
                          }}>
                              Open
                          </span>
                      </div>
                  </div>

                  <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginRight: '10px',
                      marginBottom: '-16px',
                      height: '100%'
                  }}>
                      <span style={{
                          fontSize: '12px',
                          fontWeight: 400,
                          lineHeight: '16px',
                          color: '#66717B'
                      }}>
                            Use “✱” as a wildcard at start or end of search query.
                        </span>
                  </div>
              </div>
          </div>

          {/* Right Section */}
          <div style={{
              display: 'flex',
              width: '256px',
              padding: '16px',
              flexDirection: 'column',
              justifyContent: (hoveredItem || selectedItem) ? 'flex-start' : 'center',
              alignItems: (hoveredItem || selectedItem) ? 'flex-start' : 'center',
              gap: (hoveredItem || selectedItem) ? '0' : '8px',
              flexShrink: 0,
              alignSelf: 'stretch',
              borderRadius: '0 16px 16px 0',
              background: '#F7F8FA',
              overflowY: 'auto',
              overflowX: 'hidden',
               position: 'relative',

    /* HIDE SCROLLBAR */
    scrollbarWidth: 'none',
    msOverflowStyle: 'none', 
}}>

              {!(hoveredItem || selectedItem) ? (
                  <>
                      <Icon type="search" style={{ fontSize: '48px', color: '#bfbfbf' }} />
                      <div style={{
                          alignSelf: 'stretch',
                          color: '#99AAB9',
                          textAlign: 'center',
                          fontFamily: 'Lato',
                          fontSize: '16px',
                          fontWeight: 700,
                          lineHeight: '24px'
                      }}>
                          Start Typing to Search
                      </div>
                  </>
              ) : (
                  <>
                      {/* Header Section */}
                      <div style={{
                          alignSelf: 'stretch',
                          paddingBottom: '16px',
                          marginBottom: '8px',
                          position: 'relative'
                      }}>
                          {/* Icon - Top Right Corner */}
                          <div style={{
                              display: 'flex',
                              width: '40px',
                              height: '40px',
                              justifyContent: 'center',
                              alignItems: 'center',
                              borderRadius: '8px',
                              position: 'absolute',
                              top: '0',
                              right: '0',
                          }}>
                              {getDocumentIcon(hoveredItem || selectedItem, orderConfig?.enhancedOrderTypes, omtByOrderLabel)}
                          </div>

                          {/* Item Type Label */}
                          <div style={{
                              color: '#66717B',
                              fontFamily: 'Lato',
                              fontSize: '11px',
                              fontWeight: 600,
                              lineHeight: '16px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              marginBottom: '8px'
                          }}>
                              {(hoveredItem || selectedItem)?.document_type_display ||
                                  (hoveredItem || selectedItem)?.document_type ||
                                  (hoveredItem || selectedItem)?.itemType}
                          </div>

                          {/* Item Number - Using Identity Value */}
                          <div style={{
                              color: '#111314',
                              fontFamily: 'Lato',
                              fontSize: '18px',
                              fontWeight: 700,
                              lineHeight: '24px',
                              paddingRight: '48px',
                              wordWrap: 'break-word',
                              wordBreak: 'break-word'
                          }}>
                              {(() => {
                                  const currentItem = hoveredItem || selectedItem;
                                  const identityValue = getIdentityValue(currentItem || {});
                                  return (
                                      <span>
                                          {`# ${identityValue}`}
                                          {isDeletedOrderResult(currentItem) && (
                                              <span style={{ color: '#C24141', marginLeft: 8, fontWeight: 700 }}>Deleted</span>
                                          )}
                                      </span>
                                  );
                              })()}
                          </div>
                      </div>

                      {(hoveredItem || selectedItem)?.projectable_fields && Object.entries((hoveredItem || selectedItem).projectable_fields)
                          .filter(([key, value]) => {
                              if (!value) return false;
                              const lowerKey = key.toLowerCase();
                              const currentItem = hoveredItem || selectedItem;
                              const docType = currentItem?.document_type || '';
                              
                              if (shouldExcludeField(key, docType)) return false;
                              return !EXCLUDED_FIELD_KEYS.includes(lowerKey);
                          })
                          .map(([key, value], index) => {
                              const displayValue = Array.isArray(value) 
                                  ? value.join(', ') 
                                  : value;
                              
                              const currentItem = hoveredItem || selectedItem;
                              const docType = currentItem?.document_type || currentItem?.document_type_display || '';
                              const fields = currentItem?.projectable_fields || {};
                              
                              const displayName = currentItem?.display_names?.[key] || formatFieldName(key);
                              
                              // Check if this is a driver field with dispatch capability
                              const isPickupDriver = key === 'pickup_driver_id';
                              const isDeliveryDriver = key === 'delivery_driver_id';
                              
                              let canRedirectToDispatch = false;
                              let legType = null;
                              
                              // Check if document type is customer_orders
                              const isCustomerOrder = docType === 'customer_orders' || docType.toLowerCase().includes('customer order');
                              
                              if (isCustomerOrder) {
                                  if (isPickupDriver) {
                                      const pickupStatus = fields['pickup_status'];
                                      const generalStatus = fields['status'];
                                      const hasValidStatus = pickupStatus === 'COMPLETED' || ['ASSIGNED', 'DISPATCHED', 'COMPLETED'].includes(generalStatus);
                                      
                                      if (hasValidStatus) {
                                      canRedirectToDispatch = true;
                                      legType = 'pickup';
                                      }
                                  } else if (isDeliveryDriver) {
                                      const deliveryStatus = fields['delivery_status'];
                                      const generalStatus = fields['status'];
                                      const hasValidStatus = deliveryStatus === 'COMPLETED' || ['ASSIGNED', 'DISPATCHED', 'COMPLETED'].includes(generalStatus);
                                      
                                      if (hasValidStatus) {
                                      canRedirectToDispatch = true;
                                      legType = 'delivery';
                                      }
                                  }
                              }
                              
                              const fieldIndex = index;
                              
                              return (
                                  <DetailItem
                                      key={key}
                                      label={displayName}
                                      value={displayValue}
                                      tabIndex={100 + index}
                                      isDriverField={canRedirectToDispatch}
                                      onDriverClick={canRedirectToDispatch ? () => navigateToDispatch(currentItem, legType) : null}
                                      fieldKey={key}
                                      isFocused={focusedElement === 'preview' && focusedPreviewIndex === fieldIndex}
                                      copyableRef={(el) => {
                                          previewFieldsRefs.current[fieldIndex] = el;
                                      }}
                                      onFocus={() => {
                                          setFocusedElement('preview');
                                          setFocusedPreviewIndex(fieldIndex);
                                      }}
                                      onKeyDown={(e) => {
                                          handleKeyDown(e);
                                      }}
                                  />
                              );
                          })}

                      {(hoveredItem || selectedItem)?.details && Object.entries((hoveredItem || selectedItem).details)
                          .filter(([key, value]) => {
                              if (value === null || value === undefined || value === '') return false;
                              const currentItem = hoveredItem || selectedItem;
                              const docType = currentItem?.document_type || '';
                              
                              if (shouldExcludeField(key, docType)) return false;
                              return true;
                          })
                          .map(([key, value], index) => {
                              const currentItem = hoveredItem || selectedItem;
                              const docType = currentItem?.document_type || '';
                              const displayName = currentItem?.display_names?.[key] || formatFieldName(key);
                              
                              const projectableFieldsCount = (hoveredItem || selectedItem)?.projectable_fields 
                                  ? Object.entries((hoveredItem || selectedItem).projectable_fields)
                                      .filter(([k, v]) => {
                                          if (!v) return false;
                                          const lowerKey = k.toLowerCase();
                                          if (shouldExcludeField(k, docType)) return false;
                                          return !EXCLUDED_FIELD_KEYS.includes(lowerKey);
                                      }).length
                                  : 0;
                              
                              const fieldIndex = projectableFieldsCount + index;
                              
                              return (
                                  <DetailItem
                                      key={key}
                                      label={displayName}
                                      value={value}
                                      tabIndex={100 + fieldIndex}
                                      fieldKey={key}
                                      isFocused={focusedElement === 'preview' && focusedPreviewIndex === fieldIndex}
                                      copyableRef={(el) => {
                                          previewFieldsRefs.current[fieldIndex] = el;
                                      }}
                                      onFocus={() => {
                                          setFocusedElement('preview');
                                          setFocusedPreviewIndex(fieldIndex);
                                      }}
                                      onKeyDown={(e) => {
                                          handleKeyDown(e);
                                      }}
                                  />
                              );
                          })}
                  </>
              )}
          </div>
      </div>
  );
};

function GlobalSearch (props) {
  const { filterPlaceHolder, handleFilterValueChange, handleFilterPlaceHolderChange, handleSearch, handleSaveNSearch, handleVisibleChange, filterWindow, landingPage, currentUser, handleOnChange, isShipper, orderNumberSearch, WarehouseContext, onClose, history } = props;
  const [ searchText , setSearchText ] = useState(filterPlaceHolder.search_order_token)
  const [ searchType, setSearchType ] = useState(filterPlaceHolder.search_type)  
  var currentPath = window.location.pathname;
  var isOrderRelatedScreen = /^\/orders(\/|$)/.test(currentPath);
  const { filterPlaceHolder : savedFilter , setFilterPlaceHolder } = useContext(OrderConfigContext);

  useEffect(() => {
      setSearchText(filterPlaceHolder.search_order_token)
  }, [filterPlaceHolder.search_order_token])

  useEffect(() => {
    if (!isOrderRelatedScreen) {
      // setIssearched(false);
      setSearchText("");
      setFilterPlaceHolder({
        ...savedFilter,
        search_key_value: "",
        search_order_token: "",
      });
    }
  }, [isOrderRelatedScreen]);

  return (
      <div style={{
          display: 'flex',
          width: '100%',
          height: '550px',
          alignItems: 'flex-start',
          overflow: 'hidden',
          background: '#FFF'
      }}>
        <SearchOptions
        history={props.history}
          filter={ filterPlaceHolder }
          handleFilterPlaceHolderChange={ handleFilterPlaceHolderChange }
          elementSize="default"
          onSearch={ handleSearch }
          setSearchType={ setSearchType }
          searchType={ searchType }
          searchText={ searchText }
          closeFilterWindow={ () => handleOnChange("filterWindow", false) }
          setSearchText={ setSearchText }
              orderNumberSearch={orderNumberSearch}
                    handleFilterValueChange={ handleFilterValueChange }
                    WarehouseContext={ WarehouseContext }
              onClose={onClose}
                  />
      </div>
  )
}

export default withRouter(GlobalSearch);